// Inspired by https://www.digitalocean.com/community/tutorials/how-to-build-forms-in-react
import { useMemo, useReducer, useState } from "react"
import PropTypes from "prop-types"
import useIsMounted from "react-is-mounted-hook"
import moment from "moment"
import { validate as validateEmail } from "email-validator"
import { DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from "@mui/material"
import { chain, isEmpty, map, get, mapValues } from "lodash"
import { FIELD, ANALYTICS_TRACKING } from "helpers/shapes"
import { useTrack } from "helpers/analytics"
import { formatShortDate } from "helpers/formatters/dates"
import Field from "./field"

const formReducer = (state, update) => ({
  ...state,
  ...update,
})

const touchReducer = (state, field) => ({
  ...state,
  [field]: true,
})

const isEmptyArray = (obj) => Array.isArray(obj) && isEmpty(obj)

const VALIDATIONS = [{
  name: "required",
  runOn: field => get(field, "validations.isRequired"),
  check: value => !value || isEmptyArray(value),
  message: field => `${field.label} is required`,
}, {
  name: "minLength",
  runOn: field => get(field, "validations.minLength"),
  check: (value, field) => value && value.length < field.validations.minLength,
  message: field => `${field.label} must be at least ${field.validations.minLength} characters long`,
}, {
  name: "maxLength",
  runOn: field => get(field, "validations.maxLength"),
  check: (value, field) => value && value.length > field.validations.maxLength,
  message: field => `${field.label} cannot be more than ${field.validations.maxLength} characters long`,
}, {
  name: "email",
  runOn: field => field.type === "email",
  check: value => value && !validateEmail(value),
  message: field => `${field.label} must be a valid email`,
}, {
  name: "date",
  runOn: field => field.type === "date",
  check: value => moment.isMoment(value) && !value.isValid(),
  message: field => `${field.label} must be a valid date`,
}, {
  name: "minDate",
  runOn: field => field.type === "date" && get(field, "validations.minDate"),
  check: (value, field) => moment.isMoment(value) && value.isValid() && value.isBefore(field.validations.minDate),
  message: field => `${field.label} must be on or after ${formatShortDate(field.validations.minDate)}`,
}]

function validateField(field, value) {
  for (const { runOn, check, message } of VALIDATIONS) {
    const errorMessage = runOn(field) && check(value, field) && message(field)
    if (errorMessage) {
      return {
        id: field.id,
        message: errorMessage,
      }
    }
  }
}

export default function Form({ title, message, fields, editedItem, getInitialValues, additionalValues, onClose, onSubmit, submitText, track }) {
  const initialValues = editedItem && getInitialValues ? getInitialValues(editedItem) : {}
  const formMessage = message && typeof message === "function" ? message(editedItem) : message

  const isMounted = useIsMounted()
  const [formData, setFormData] = useReducer(formReducer, initialValues)
  const [touched, touch] = useReducer(touchReducer, {})
  const [submitError, setSubmitError] = useState(null)
  const [isSubmitting, setSubmitting] = useState(false)

  const trackSubmit = useTrack(track)

  function submit() {
    setSubmitError(null)
    setSubmitting(true)
    const submissionData = {
      ...additionalValues,
      ...mapValues(formData, datum => moment.isMoment(datum) ? datum.format("YYYY-MM-DD") : datum),
    }
    onSubmit(submissionData, (errorMessage) => {
      if (isMounted()) {
        setSubmitting(false)
        setSubmitError(errorMessage)
      }
      if (!errorMessage) {
        onClose()
        trackSubmit(submissionData)
      }
    })
  }

  const errorMessages = useMemo(() => {
    return chain(fields)
      .map(field => validateField(field, formData[field.id]))
      .filter(m => !!m)
      .keyBy("id")
      .mapValues(m => m.message)
      .value()
  }, [formData, fields])

  const onFormSubmit = (event) => {
    event.preventDefault()
    if (isEmpty(errorMessages)) {
      submit()
    }
  }

  return <>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent style={{ minWidth: 400 }}>
      {formMessage && <DialogContent>
        {formMessage}
      </DialogContent> }
      {submitError && <DialogContentText color="error" className="error">
        {submitError}
      </DialogContentText> }
      <form style={{ display: "flex", flexDirection: "column" }} onSubmit={onFormSubmit}>
        {map(fields, field => <Field
          key={field.id}
          {...field}
          formData={formData}
          setFormData={setFormData}
          error={errorMessages[field.id]}
          touch={touch}
          required={get(field, "validations.isRequired", false)}
          validations={field.validations}
          isTouched={!!touched[field.id]}
        />)}
      </form>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary">
        Cancel
      </Button>
      <Button
        onClick={submit}
        color="primary"
        disabled={!isEmpty(errorMessages) && !isSubmitting}
      >
        {isSubmitting ? "Please Wait" : submitText}
      </Button>
    </DialogActions>
  </>
}

Form.propTypes = {
  title: PropTypes.string.isRequired,
  message: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.func,
  ]),
  fields: PropTypes.arrayOf(FIELD).isRequired,
  editedItem: PropTypes.object,
  getInitialValues: PropTypes.func,
  additionalValues: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitText: PropTypes.string.isRequired,
  track: ANALYTICS_TRACKING,
}
